import { supabase } from '@/integrations/supabase/client';
import { ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import { DNAInheritanceService } from '@/services/dna-inheritance.service';
import type { CampaignMetadata } from '@/types/content-dna';
import type { Json } from '@/integrations/supabase/types';
import type { CampaignWithPosts } from '@/types/database';

interface PostAnalyticsRecord {
  clicks: number | null;
  comments: number | null;
  engagement_rate: number | null;
  likes: number | null;
  shares: number | null;
  recorded_at: string;
}

interface PostWithAnalytics {
  id: string;
  content: string;
  platform: string;
  status: string;
  scheduled_for: string | null;
  created_at: string;
  post_analytics: PostAnalyticsRecord[];
}

export interface CampaignAnalytics {
  campaignId: string;
  campaignName: string;
  totalPosts: number;
  publishedPosts: number;
  totalEngagement: number;
  totalImpressions: number;
  averageEngagementRate: number;
  clickThroughRate: number;
  estimatedReach: number;
  dnaSource: 'organization' | 'generic';
  dnaQuality: 'high' | 'medium' | 'low';
  topPerformingPost?: {
    id: string;
    content: string;
    engagementRate: number;
  };
  platformBreakdown: Array<{
    platform: string;
    posts: number;
    engagement: number;
  }>;
  timelineData: Array<{
    date: string;
    engagement: number;
    impressions: number;
  }>;
}

export class CampaignAnalyticsService {
  private static logger = logger.child({ component: 'CampaignAnalyticsService' });

  /**
   * Get comprehensive analytics for a campaign
   */
  static async getCampaignAnalytics(campaignId: string): Promise<CampaignAnalytics | null> {
    try {
      // Fetch campaign with posts and analytics
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .select(`
          id,
          name,
          organization_id,
          metadata,
          campaign_posts!inner(
            post_id,
            posts!inner(
              id,
              content,
              platform,
              status,
              published_at,
              post_analytics(
                clicks,
                comments,
                engagement_rate,
                likes,
                shares
              )
            )
          )
        `)
        .eq('id', campaignId)
        .maybeSingle();

      if (campaignError) {
        this.logger.error('Error fetching campaign analytics', { error: campaignError });
        return null;
      }

      if (!campaign) return null;

      // Fetch LIVE DNA status from organization (not stale metadata)
      const orgId = campaign.organization_id;
      const dnaStatus = await DNAInheritanceService.checkDNAStatus(orgId);
      const dnaSource: 'organization' | 'generic' = dnaStatus.hasOrg ? 'organization' : 'generic';
      const dnaQuality: 'high' | 'medium' | 'low' = dnaStatus.hasOrg ? 'high' : 'low';

      // Calculate metrics
      const posts = campaign.campaign_posts.map(cp => cp.posts);
      const totalPosts = posts.length;
      const publishedPosts = posts.filter(p => p.status === 'published').length;

      let totalEngagement = 0;
      let totalImpressions = 0;
      let totalClicks = 0;
      const platformStats = new Map<string, { posts: number; engagement: number }>();
      const timelineMap = new Map<string, { engagement: number; impressions: number }>();

      let topPost: { id: string; content: string; engagementRate: number } | null = null;
      let maxEngagement = 0;

      posts.forEach(post => {
        // Platform breakdown
        const platform = post.platform || 'unknown';
        const platformData = platformStats.get(platform) || { posts: 0, engagement: 0 };
        platformData.posts += 1;

        // Calculate engagement from analytics
        if (post.post_analytics && post.post_analytics.length > 0) {
          post.post_analytics.forEach(analytics => {
            const engagement = (analytics.likes || 0) + (analytics.shares || 0) + (analytics.comments || 0);
            totalEngagement += engagement;
            totalClicks += analytics.clicks || 0;
            platformData.engagement += engagement;

            // Track top performing post
            if (engagement > maxEngagement) {
              maxEngagement = engagement;
              topPost = {
                id: post.id,
                content: post.content,
                engagementRate: analytics.engagement_rate || 0
              };
            }

            // Timeline data (by day)
            if (post.published_at) {
              const date = new Date(post.published_at).toISOString().split('T')[0];
              const timelineData = timelineMap.get(date) || { engagement: 0, impressions: 0 };
              timelineData.engagement += engagement;
              timelineMap.set(date, timelineData);
            }
          });
        }

        platformStats.set(platform, platformData);
      });

      const averageEngagementRate = totalImpressions > 0 
        ? (totalEngagement / totalImpressions) * 100 
        : totalEngagement > 0 ? (totalEngagement / publishedPosts) * 100 : 0;
      
      const clickThroughRate = totalImpressions > 0
        ? (totalClicks / totalImpressions) * 100
        : 0;

      // Convert maps to arrays
      const platformBreakdown = Array.from(platformStats.entries()).map(([platform, data]) => ({
        platform,
        posts: data.posts,
        engagement: data.engagement
      }));

      const timelineData = Array.from(timelineMap.entries())
        .map(([date, data]) => ({
          date,
          engagement: data.engagement,
          impressions: data.impressions
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return {
        campaignId: campaign.id,
        campaignName: campaign.name,
        totalPosts,
        publishedPosts,
        totalEngagement,
        totalImpressions,
        averageEngagementRate,
        clickThroughRate,
        estimatedReach: totalImpressions,
        dnaSource,
        dnaQuality,
        topPerformingPost: topPost ?? undefined,
        platformBreakdown,
        timelineData
      };
    } catch (error) {
      this.logger.error('Error calculating campaign analytics', { error });
      return null;
    }
  }

  /**
   * Compare DNA effectiveness across campaigns
   */
  static async compareDNAEffectiveness(
    organizationId: string
  ): Promise<Array<{
    dnaSource: 'organization' | 'generic';
    campaignCount: number;
    avgEngagementRate: number;
  }>> {
    try {
      const { data: campaigns, error } = await supabase
        .from('campaigns')
        .select(`
          id,
          metadata,
          campaign_posts!inner(
          posts!inner(
            post_analytics(
              clicks,
              comments,
              engagement_rate,
              likes,
              shares
            )
          )
          )
        `)
        .eq('organization_id', organizationId)
        .eq('status', 'active');

      if (error) {
        this.logger.error('Error fetching DNA effectiveness data', { error });
        return [];
      }

      const dnaStats = new Map<string, { count: number; totalEngagement: number; posts: number }>();

      campaigns?.forEach(campaign => {
        const metadata = (campaign.metadata as CampaignMetadata) || {};
        const dnaSource = (metadata as Record<string, unknown>).dna_source as string || 'generic';
        
        const stats = dnaStats.get(dnaSource) || { count: 0, totalEngagement: 0, posts: 0 };
        stats.count += 1;

        // Type narrowing for campaign_posts access
        const typedCampaign = campaign as CampaignWithPosts;
        typedCampaign.campaign_posts?.forEach(cp => {
          cp.posts?.post_analytics?.forEach(analytics => {
            const engagement = (analytics.likes || 0) + (analytics.shares || 0) + (analytics.comments || 0);
            stats.totalEngagement += engagement;
            stats.posts += 1;
          });
        });

        dnaStats.set(dnaSource, stats);
      });

      return Array.from(dnaStats.entries()).map(([dnaSource, stats]) => ({
        dnaSource: dnaSource as 'organization' | 'generic',
        campaignCount: stats.count,
        avgEngagementRate: stats.posts > 0 ? stats.totalEngagement / stats.posts : 0
      }));
    } catch (error) {
      this.logger.error('Error comparing DNA effectiveness', { error });
      return [];
    }
  }
}
